import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIsAdmin } from 'js/hooks/user';
import { identity, pick, pipe, prop } from 'ramda';
import { useAddTranslations } from 'js/hooks/i18n';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { getFriendlyUrl, getCleanPath } from 'js/utils/url.util';
import { useGetCMSResourceURL } from 'js/hooks/useGetCMSResourceURL';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { get } from 'js/utils/fetch.util';

import * as helpers from './helpers';
import * as actions from '../../../../store/actions';
import { useClearIdsInPath } from '../useClearIdsInPath';

export const useGetResponseHandler = (isPageInPopup) => {
    const isAdmin = useIsAdmin();
    const addTranslations = useAddTranslations();
    const role = useUserSelector(prop('role'));

    return useCallback((data, path) => {
        addTranslations({
            isPageTranslations: true,
            translations: data.translations,
            isPageInPopupTranslations: isPageInPopup
        });

        return helpers.onResponse(data, path, role, isAdmin);
    }, [addTranslations, isPageInPopup, role, isAdmin]);
};

export const usePageInfoFetch = ({ path, isPageInPopup }) => {
    const isMounted = useIsMounted();
    const getCMSResourceURL = useGetCMSResourceURL();
    const onResponse = useGetResponseHandler(isPageInPopup);
    const [{ isLoading, error: pageError, data: page }, refetch] = useDataFetch({
        onResponse,
        onError: helpers.onError,
        fetch: get,
        shouldFetch: identity
    });
    const lang = useUserSelector(prop('lang'));
    const formFactor = useDeviceSelector(prop('formFactor'));
    const { rootPage, requestStamp, isChangeLangInProgress } = useAppSelector(pick([
        'rootPage', 'requestStamp', 'isChangeLangInProgress'
    ]));
    const dispatch = useDispatch();
    const [fetchPageInfoTimestamp, setFetchPageInfoTimestamp] = useState(requestStamp);
    const clearIdsInPath = useClearIdsInPath();
    const pagePath = path && path !== '/' ? path : rootPage;
    const preparePageFetchPath = pipe(clearIdsInPath, getFriendlyUrl);
    const pageFetchPath = preparePageFetchPath(pagePath);
    const cleanPath = getCleanPath(pagePath);

    useEffect(() => {
        if (!page || isPageInPopup) {
            return;
        }

        dispatch(actions.setPageData({ path, page }));
    }, [path, page, dispatch, isPageInPopup]);

    useEffect(() => {
        if (!isMounted() || isChangeLangInProgress) {
            return;
        }

        const pageDescription = `${lang}_${formFactor}`;
        const resourceURL = getCMSResourceURL(`/pageInfo/${pageFetchPath}/${pageDescription}`, { t: fetchPageInfoTimestamp });

        refetch(resourceURL, cleanPath);
    }, [
        fetchPageInfoTimestamp, cleanPath, pageFetchPath, formFactor, requestStamp, refetch,
        lang, isChangeLangInProgress, getCMSResourceURL, isMounted
    ]);

    return [{ pageError, isLoading, page }, setFetchPageInfoTimestamp];
};
